import { api } from './api';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface TutorQuizConfigDTO {
  maxQuestions?: number;
  timeLimitMinutes?: number;
  minPassPercent?: number;
  maxRetries?: number;
  allowAiGeneration?: boolean;
  allowedQuestionTypes?: string;
  hasCourseConfig?: boolean;
}

export interface TutorResolvedConfigDTO extends TutorQuizConfigDTO {
  isOverride?: boolean;
  source?: string;
  hasCourseConfig?: boolean;
}

export interface TutorQuizOptionDTO {
  id: number;
  questionId: number;
  optionText: string;
  isCorrect: boolean;
  displayOrder: number;
}

export interface TutorQuizQuestionDTO {
  id: number;
  quizId: number;
  questionText: string;
  questionType: string;
  displayOrder: number;
  explanation?: string;
  options: TutorQuizOptionDTO[];
}

export interface TutorQuizDTO {
  id: number;
  courseId: number;
  nodeId: number;
  title: string;
  status: string;
  maxQuestions?: number;
  timeLimitMinutes?: number;
  minPassPercent?: number;
  createdAt: string;
  questions: TutorQuizQuestionDTO[];
}

export interface TutorQuizSummaryDTO {
  id: number;
  title: string;
  status: string;
  questionCount: number;
  timeLimitMinutes?: number;
  minPassPercent?: number;
  createdAt: string;
}

export interface TutorGenerationJobDTO {
  jobId: number;
  jobType: string;
  status: string;
  totalTopics: number;
  completedTopics: number;
  failedTopics?: number;
  quizzesPerTopic?: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface TutorQuizReviewItemDTO {
  quizId: number;
  nodeId: number;
  nodeLabel: string;
  title: string;
  questionCount: number;
  timeLimitMinutes?: number;
  questions: TutorQuizQuestionDTO[];
}

export interface TutorContentReviewItemDTO {
  draftId: number;
  nodeId: number;
  nodeLabel: string;
  draftContent: string;
  status?: string;
  createdAt: string;
}

// ─── Quiz Config: Course-level ────────────────────────────────────────────────

export async function getCourseQuizConfig(courseId: number): Promise<TutorQuizConfigDTO> {
  const res = await api.get<{ data: TutorQuizConfigDTO }>(`/api/tutor/courses/${courseId}/quiz-config`);
  return res.data.data;
}

export async function saveCourseQuizConfig(courseId: number, config: TutorQuizConfigDTO): Promise<TutorQuizConfigDTO> {
  const res = await api.put<{ data: TutorQuizConfigDTO }>(`/api/tutor/courses/${courseId}/quiz-config`, config);
  return res.data.data;
}

export async function deleteCourseQuizConfig(courseId: number): Promise<void> {
  await api.delete(`/api/tutor/courses/${courseId}/quiz-config`);
}

// ─── Quiz Config: Node-level ──────────────────────────────────────────────────

export async function getNodeQuizConfig(courseId: number, nodeId: number): Promise<TutorResolvedConfigDTO> {
  const res = await api.get<{ data: TutorResolvedConfigDTO }>(`/api/tutor/courses/${courseId}/nodes/${nodeId}/quiz-config`);
  return res.data.data;
}

export async function saveNodeQuizConfig(courseId: number, nodeId: number, config: TutorQuizConfigDTO): Promise<TutorQuizConfigDTO> {
  const res = await api.put<{ data: TutorQuizConfigDTO }>(`/api/tutor/courses/${courseId}/nodes/${nodeId}/quiz-config`, config);
  return res.data.data;
}

export async function deleteNodeQuizConfig(courseId: number, nodeId: number): Promise<TutorResolvedConfigDTO> {
  const res = await api.delete<{ data: TutorResolvedConfigDTO }>(`/api/tutor/courses/${courseId}/nodes/${nodeId}/quiz-config`);
  return res.data.data;
}

// ─── Quizzes: Per-node ────────────────────────────────────────────────────────

export async function listNodeQuizzes(courseId: number, nodeId: number): Promise<TutorQuizSummaryDTO[]> {
  const res = await api.get<{ data: TutorQuizSummaryDTO[] }>(`/api/tutor/courses/${courseId}/nodes/${nodeId}/quizzes`);
  return res.data.data;
}

export async function getNodeQuiz(courseId: number, nodeId: number, quizId: number): Promise<TutorQuizDTO> {
  const res = await api.get<{ data: TutorQuizDTO }>(`/api/tutor/courses/${courseId}/nodes/${nodeId}/quizzes/${quizId}`);
  return res.data.data;
}

export async function generateQuiz(courseId: number, nodeId: number, questionTypesOverride?: string): Promise<TutorQuizDTO> {
  const body = questionTypesOverride ? { questionTypesOverride } : {};
  const res = await api.post<{ data: TutorQuizDTO }>(`/api/tutor/courses/${courseId}/nodes/${nodeId}/quizzes/generate`, body);
  return res.data.data;
}

export async function acceptQuiz(courseId: number, nodeId: number, quizId: number): Promise<TutorQuizSummaryDTO> {
  const res = await api.post<{ data: TutorQuizSummaryDTO }>(`/api/tutor/courses/${courseId}/nodes/${nodeId}/quizzes/${quizId}/accept`);
  return res.data.data;
}

export async function deleteQuiz(courseId: number, nodeId: number, quizId: number): Promise<void> {
  await api.delete(`/api/tutor/courses/${courseId}/nodes/${nodeId}/quizzes/${quizId}`);
}

// ─── Batch Generation Jobs ────────────────────────────────────────────────────

export async function startBatchQuizGeneration(courseId: number, quizzesPerTopic: number): Promise<TutorGenerationJobDTO> {
  const res = await api.post<{ data: TutorGenerationJobDTO }>(`/api/tutor/courses/${courseId}/generation-jobs/quiz`, { quizzesPerTopic });
  return res.data.data;
}

export async function startBatchContentGeneration(courseId: number): Promise<TutorGenerationJobDTO> {
  const res = await api.post<{ data: TutorGenerationJobDTO }>(`/api/tutor/courses/${courseId}/generation-jobs/content`, {});
  return res.data.data;
}

export async function getJobStatus(courseId: number, jobId: number): Promise<TutorGenerationJobDTO> {
  const res = await api.get<{ data: TutorGenerationJobDTO }>(`/api/tutor/courses/${courseId}/generation-jobs/${jobId}`);
  return res.data.data;
}

export async function listJobs(courseId: number): Promise<TutorGenerationJobDTO[]> {
  const res = await api.get<{ data: TutorGenerationJobDTO[] }>(`/api/tutor/courses/${courseId}/generation-jobs`);
  return res.data.data;
}

// ─── Quiz Review Queue ────────────────────────────────────────────────────────

export async function listQuizReview(courseId: number): Promise<TutorQuizReviewItemDTO[]> {
  const res = await api.get<{ data: TutorQuizReviewItemDTO[] }>(`/api/tutor/courses/${courseId}/quiz-review`);
  return res.data.data;
}

export async function acceptReviewQuiz(courseId: number, quizId: number): Promise<void> {
  await api.post(`/api/tutor/courses/${courseId}/quiz-review/${quizId}/accept`);
}

export async function rejectReviewQuiz(courseId: number, quizId: number): Promise<void> {
  await api.post(`/api/tutor/courses/${courseId}/quiz-review/${quizId}/reject`);
}

export async function acceptAllReviewQuizzes(courseId: number): Promise<{ accepted: number }> {
  const res = await api.post<{ data: { accepted: number } }>(`/api/tutor/courses/${courseId}/quiz-review/accept-all`);
  return res.data.data;
}

// ─── Content Review Queue ─────────────────────────────────────────────────────

export async function listContentReview(courseId: number): Promise<TutorContentReviewItemDTO[]> {
  const res = await api.get<{ data: TutorContentReviewItemDTO[] }>(`/api/tutor/courses/${courseId}/content-review`);
  return res.data.data;
}

export async function acceptContentDraft(courseId: number, draftId: number): Promise<void> {
  await api.post(`/api/tutor/courses/${courseId}/content-review/${draftId}/accept`);
}

export async function rejectContentDraft(courseId: number, draftId: number): Promise<void> {
  await api.post(`/api/tutor/courses/${courseId}/content-review/${draftId}/reject`);
}

export async function acceptAllContentDrafts(courseId: number): Promise<{ accepted: number }> {
  const res = await api.post<{ data: { accepted: number } }>(`/api/tutor/courses/${courseId}/content-review/accept-all`);
  return res.data.data;
}

// ─── Course Quiz Overview ─────────────────────────────────────────────────────

export interface TopicGapDTO {
  nodeId: number;
  label: string;
}

export async function getTopicGaps(courseId: number): Promise<TopicGapDTO[]> {
  const res = await api.get<{ data: TopicGapDTO[] }>(`/api/tutor/courses/${courseId}/topic-gaps`);
  return res.data.data;
}
